import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Package,
  AlertTriangle,
  TrendingUp,
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle,
  ArrowUpRight,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ProofImageGallery from "@/components/ProofImageGallery";
import { PageHeader, PageShell } from "@/components/PageShell";
import { PageEmptyState, PageLoadingState, SectionLoadingState } from "@/components/PageStates";
import { formatRupiah } from "@/lib/currency";
import { buildProofImageSrc, getTransactionProofImageUrls } from "@/lib/proofImages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toInputDate = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 6);
  return {
    startDate: toInputDate(start),
    endDate: toInputDate(end),
  };
};

const getPresetDateRange = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  return {
    startDate: toInputDate(start),
    endDate: toInputDate(end),
  };
};

const getRangeBoundaries = (startDate, endDate) => {
  const start = startDate ? new Date(`${startDate}T00:00:00`) : null;
  const end = endDate ? new Date(`${endDate}T23:59:59.999`) : null;
  return { start, end };
};

const DashboardPage = () => {
  const [stats, setStats] = useState({
    total_barang: 0,
    stok_menipis: 0,
    transaksi_hari_ini: 0,
    sisa_modal: 0,
  });
  const [loading, setLoading] = useState(true);
  const [masterLoading, setMasterLoading] = useState(false);
  const [activeHistory, setActiveHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState({
    masuk: [],
    keluar: [],
  });
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [suppliers, setSuppliers] = useState([]);
  const [barangs, setBarangs] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchMasterData();
  }, []);

  const supplierMap = useMemo(
    () => Object.fromEntries((suppliers || []).map((s) => [s.supplier_id, s.nama_supplier])),
    [suppliers]
  );

  const barangMap = useMemo(
    () => Object.fromEntries((barangs || []).map((b) => [b.barang_id, b.nama_barang])),
    [barangs]
  );

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch {
      toast.error("Gagal memuat statistik");
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterData = async () => {
    setMasterLoading(true);
    try {
      const [supplierRes, barangRes] = await Promise.all([
        axios.get(`${API}/supplier`),
        axios.get(`${API}/barang`),
      ]);
      setSuppliers((supplierRes.data || []).filter((s) => s.aktif !== false));
      setBarangs((barangRes.data || []).filter((b) => b.aktif !== false));
    } catch {
      toast.error("Gagal memuat data supplier dan barang");
    } finally {
      setMasterLoading(false);
    }
  };

  const fetchHistory = useCallback(async (type) => {
    setHistoryLoading(true);
    try {
      const endpoint = type === "masuk" ? "barang-masuk" : "barang-keluar";
      const response = await axios.get(`${API}/${endpoint}`, {
        params: {
          limit: 20,
          offset: 0,
          start_date: dateRange.startDate || undefined,
          end_date: dateRange.endDate || undefined,
        },
      });
      setHistoryData((prev) => ({
        ...prev,
        [type]: response.data || [],
      }));
    } catch {
      toast.error(type === "masuk" ? "Gagal memuat riwayat barang masuk" : "Gagal memuat riwayat barang keluar");
    } finally {
      setHistoryLoading(false);
    }
  }, [dateRange.endDate, dateRange.startDate]);

  useEffect(() => {
    if (!activeHistory) {
      return;
    }
    fetchHistory(activeHistory);
  }, [activeHistory, fetchHistory]);

  const formatTanggal = (tanggal) => {
    if (!tanggal) {
      return "-";
    }
    return new Date(tanggal).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSupplierName = (supplierId) => supplierMap[supplierId] || "-";
  const getBarangName = (barangId) => barangMap[barangId] || barangId || "-";

  const isPresetActive = (days) => {
    const preset = getPresetDateRange(days);
    return preset.startDate === dateRange.startDate && preset.endDate === dateRange.endDate;
  };

  const getItemSummary = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) {
      return "Tanpa item";
    }
    return items
      .slice(0, 2)
      .map((item) => `${getBarangName(item.barang_id)} (${item.qty_input ?? item.qty} ${item.satuan_input || ""})`)
      .join(", ") + (items.length > 2 ? ` +${items.length - 2} item lain` : "");
  };

  const isTransactionInRange = (tanggal) => {
    if (!tanggal) {
      return false;
    }

    const trxDate = new Date(tanggal);
    if (Number.isNaN(trxDate.getTime())) {
      return false;
    }

    const { start, end } = getRangeBoundaries(dateRange.startDate, dateRange.endDate);

    if (start && trxDate < start) {
      return false;
    }

    if (end && trxDate > end) {
      return false;
    }

    return true;
  };

  const statCards = [
    {
      title: "Total Barang",
      value: stats.total_barang,
      icon: Package,
      color: "text-primary",
      bg: "bg-primary/10",
    },
    {
      title: "Stok Menipis",
      value: stats.stok_menipis,
      icon: AlertTriangle,
      color: "text-destructive",
      bg: "bg-destructive/10",
    },
    {
      title: "Transaksi Hari Ini",
      value: stats.transaksi_hari_ini,
      icon: TrendingUp,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      title: "Sisa Modal",
      value: formatRupiah(stats.sisa_modal),
      icon: Wallet,
      color: "text-green-600",
      bg: "bg-green-50",
    },
  ];

  const currentHistoryRows = activeHistory
    ? (historyData[activeHistory] || []).filter((row) => isTransactionInRange(row.tanggal))
    : [];

  const getDisplayMasukTotalBelanja = (transaksiItem) => {
    return Number(transaksiItem?.total_belanja ?? 0);
  };

  const selectedHistoryDetailItems =
    activeHistory === "masuk" ? selectedHistory?.items || [] : selectedHistory?.items || [];
  const selectedHistoryFotoGalleryItems = useMemo(
    () =>
      getTransactionProofImageUrls(selectedHistory).map((url, index) => {
        const imageSrc = buildProofImageSrc(url);
        return {
          id: `${activeHistory || "history"}-${selectedHistory?.pembelian_id || selectedHistory?.keluar_id || index}`,
          src: imageSrc,
          href: imageSrc,
          alt: `Foto bukti riwayat ${index + 1}`,
          label: `Foto ${index + 1}`,
        };
      }),
    [activeHistory, selectedHistory]
  );

  if (loading) {
    return <PageLoadingState label="Memuat dashboard" description="Menyiapkan ringkasan inventory dan riwayat transaksi terbaru." />;
  }

  return (
    <PageShell data-testid="dashboard-page" className="space-y-8">
      <PageHeader
        eyebrow="Ringkasan Operasional"
        title="Dashboard"
        description="Pantau stok, transaksi, dan kondisi modal dapur dalam satu tampilan yang lebih mudah dipindai."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div
              key={index}
              data-testid={`stat-card-${index}`}
              className="rounded-lg border bg-card text-card-foreground shadow-sm p-6 transition-all hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-manrope font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    {card.title}
                  </p>
                  <p className="text-3xl font-manrope font-bold tabular-nums tracking-tight text-slate-900">
                    {card.value}
                  </p>
                </div>
                <div className={`${card.bg} ${card.color} p-3 rounded-lg`}>
                  <Icon size={24} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <button
          type="button"
          onClick={() => setActiveHistory("masuk")}
          className={`rounded-lg border bg-card text-card-foreground shadow-sm p-6 text-left transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
            activeHistory === "masuk" ? "ring-2 ring-green-500/60" : ""
          }`}
          aria-pressed={activeHistory === "masuk"}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-green-50 text-green-600 p-2 rounded-lg">
              <ArrowDownCircle size={20} />
            </div>
            <h3 className="font-manrope font-bold text-lg">Barang Masuk</h3>
          </div>
          <p className="text-muted-foreground">Klik untuk melihat riwayat barang masuk</p>
        </button>

        <button
          type="button"
          onClick={() => setActiveHistory("keluar")}
          className={`rounded-lg border bg-card text-card-foreground shadow-sm p-6 text-left transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
            activeHistory === "keluar" ? "ring-2 ring-orange-500/60" : ""
          }`}
          aria-pressed={activeHistory === "keluar"}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-orange-50 text-orange-600 p-2 rounded-lg">
              <ArrowUpCircle size={20} />
            </div>
            <h3 className="font-manrope font-bold text-lg">Barang Keluar</h3>
          </div>
          <p className="text-muted-foreground">Klik untuk melihat riwayat barang keluar</p>
        </button>
      </div>

      {activeHistory && (
        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <h3 className="font-manrope font-bold text-xl">
              {activeHistory === "masuk" ? "Riwayat Barang Masuk" : "Riwayat Barang Keluar"}
            </h3>
            <a
              href={activeHistory === "masuk" ? "/barang-masuk?view=standalone" : "/barang-keluar?view=standalone"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              Lihat semua
              <ArrowUpRight size={14} />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="md:col-span-2 flex flex-wrap gap-2">
              <Button
                type="button"
                variant={isPresetActive(1) ? "default" : "outline"}
                size="sm"
                onClick={() => setDateRange(getPresetDateRange(1))}
              >
                Hari ini
              </Button>
              <Button
                type="button"
                variant={isPresetActive(7) ? "default" : "outline"}
                size="sm"
                onClick={() => setDateRange(getPresetDateRange(7))}
              >
                7 hari
              </Button>
              <Button
                type="button"
                variant={isPresetActive(30) ? "default" : "outline"}
                size="sm"
                onClick={() => setDateRange(getPresetDateRange(30))}
              >
                30 hari
              </Button>
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Tanggal awal</p>
              <Input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Tanggal akhir</p>
              <Input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          </div>

          {(masterLoading || historyLoading) && <SectionLoadingState label="Memuat ringkasan riwayat transaksi" className="min-h-[150px]" />}

          {!masterLoading && !historyLoading && currentHistoryRows.length === 0 && (
            <PageEmptyState
              compact
              title="Belum ada riwayat transaksi"
              description="Coba ubah rentang tanggal untuk melihat transaksi barang masuk atau keluar yang tersedia."
            />
          )}

          {!masterLoading && !historyLoading && currentHistoryRows.length > 0 && (
            <div className="space-y-3">
              {currentHistoryRows.map((item) => (
                <button
                  key={item.pembelian_id || item.keluar_id}
                  type="button"
                  onClick={() => {
                    setSelectedHistory(item);
                    setDetailDialogOpen(true);
                  }}
                  className="w-full rounded-md border p-4 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      {activeHistory === "masuk" ? (
                        <p className="font-manrope font-semibold text-slate-900">
                          Supplier: {getSupplierName(item.supplier_id)}
                        </p>
                      ) : (
                        <p className="font-manrope font-semibold text-slate-900">
                          Petugas: {item.petugas || "-"}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground">{formatTanggal(item.tanggal)}</p>
                    </div>
                      {activeHistory === "masuk" && (
                        <p className="font-manrope font-bold text-primary">
                        {formatRupiah(Number(getDisplayMasukTotalBelanja(item) || 0))}
                        </p>
                      )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">Item: {getItemSummary(item.items)}</p>
                  {activeHistory === "masuk" && item.keterangan && (
                    <p className="text-sm mt-1 text-muted-foreground whitespace-pre-line">
                      Catatan: {item.keterangan}
                    </p>
                  )}
                  {activeHistory === "keluar" && item.keterangan && (
                    <p className="text-sm mt-1">Keterangan: {item.keterangan}</p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {activeHistory === "masuk" ? "Detail Riwayat Barang Masuk" : "Detail Riwayat Barang Keluar"}
            </DialogTitle>
          </DialogHeader>

          {selectedHistory && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Tanggal</p>
                  <p className="font-medium">{formatTanggal(selectedHistory.tanggal)}</p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">Petugas</p>
                  <p className="font-medium">{selectedHistory.petugas || "-"}</p>
                </div>

                {activeHistory === "masuk" && (
                  <>
                    <div>
                      <p className="text-sm text-muted-foreground">Supplier</p>
                      <p className="font-medium">{getSupplierName(selectedHistory.supplier_id)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Belanja</p>
                      <p className="font-medium text-primary">
                        {formatRupiah(Number(selectedHistory?.total_belanja ?? 0))}
                      </p>
                    </div>
                    {selectedHistory.bukti_nota && (
                      <div>
                        <p className="text-sm text-muted-foreground">No. Bukti/Nota</p>
                        <p className="font-medium">{selectedHistory.bukti_nota}</p>
                      </div>
                    )}
                    {selectedHistory.keterangan && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-muted-foreground">Catatan</p>
                        <p className="font-medium whitespace-pre-line">{selectedHistory.keterangan}</p>
                      </div>
                    )}
                    {selectedHistory.status && (
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <p className="font-medium">{selectedHistory.status}</p>
                      </div>
                    )}
                  </>
                )}

                {activeHistory === "keluar" && selectedHistory.keterangan && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-muted-foreground">Keterangan</p>
                    <p className="font-medium">{selectedHistory.keterangan}</p>
                  </div>
                )}
              </div>

              {selectedHistoryFotoGalleryItems.length > 0 ? (
                <ProofImageGallery
                  title={`Foto Bukti (${selectedHistoryFotoGalleryItems.length})`}
                  hint="Klik gambar untuk membuka ukuran penuh. Semua bukti ditampilkan dengan ukuran thumbnail yang konsisten."
                  images={selectedHistoryFotoGalleryItems}
                />
              ) : null}

              <div>
                <p className="text-sm font-manrope font-semibold mb-2">Daftar Item</p>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left">Nama Barang</th>
                        <th className="px-3 py-2 text-center">Qty</th>
                        <th className="px-3 py-2 text-left">Satuan</th>
                        {activeHistory === "masuk" && <th className="px-3 py-2 text-right">Harga</th>}
                        {activeHistory === "masuk" && <th className="px-3 py-2 text-right">Subtotal</th>}
                        {activeHistory === "keluar" && <th className="px-3 py-2 text-left">Keterangan</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedHistoryDetailItems.map((detailItem, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-3 py-2">{getBarangName(detailItem.barang_id)}</td>
                          <td className="px-3 py-2 text-center">{detailItem.qty_input ?? detailItem.qty}</td>
                          <td className="px-3 py-2">{detailItem.satuan_input || "-"}</td>
                          {activeHistory === "masuk" && (
                            <td className="px-3 py-2 text-right">
                              {formatRupiah(Number(detailItem.harga_satuan ?? 0))}
                            </td>
                          )}
                          {activeHistory === "masuk" && (
                            <td className="px-3 py-2 text-right">
                              {formatRupiah(Number(detailItem.subtotal ?? 0))}
                            </td>
                          )}
                          {activeHistory === "keluar" && (
                            <td className="px-3 py-2">{detailItem.keterangan || "-"}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="pt-2">
                <Button type="button" variant="outline" onClick={() => setDetailDialogOpen(false)}>
                  Tutup
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default DashboardPage;
